// src/context/persona/PersonaHalfForm.jsx
//
// The fields of ONE persona half. Headings and sub-labels come from the
// backend's `labels` (⛔ never hard-coded here — that copy belongs to the
// server); only the FE-owned ones (name, summary, indicators, the row buttons,
// the hints) are read from i18n.
//
// v194 — READABLE BY DESIGN. Every text box is a `GrowingTextarea` that wraps
// and grows with its text, so nothing is ever cut off at the right edge the way
// a single-line <input> cut "risponde a soci, famiglia e banche…" in the first
// manual test. The SINGLE-LINE fields (name, role, context, the three cells of
// a Pain & Gain row) stay single-line on the WIRE — Enter is blocked and a
// pasted line break becomes a space — because `personaModel.checkHalf` refuses
// a newline there (`oneLine`), and the check stays as the backstop. The
// paragraph fields (summary, the two language lists, prose Pain & Gain) keep
// real line breaks: a blank line starts a paragraph.
//
// The form is three SECTIONS (who · pain & gain · language) and, in list
// format, one CARD per Pain & Gain row: Pain and Gain side by side on a wide
// screen (stacked on a narrow one), Indicatori full width below them — a row is one thought
// (problem → expected result → how it is measured), and the old 3-column table
// read as three unrelated columns.
//
// ⛔ The Pain & Gain shape is FIXED by the data (`pain_gain.format`): rows for a
// list, one text box for prose. There is deliberately NO switch between them —
// the market decided it, and changing it is not this form's job.
//
// Controlled and stateless: every keystroke hands a whole new half to
// `onChange`. Nothing is kept locally, so the owner is the single source of
// truth and a reload after a refused save cannot leave a stale field behind.
//
// v198 — `show` = { name, summary, advanced, language } (each default true). The BASE view passes
// { advanced: false }: name, "Chi è" with the description only, Linguaggio. With `advanced` the role and the
// context appear IN PLACE inside the same "Chi è" card, below the description (the card keeps the server's
// heading), and the Pain & Gain section appears between "Chi è" and Linguaggio. The manual wizard shows one part
// per step: "Profilo" = { name: false, advanced: false }, "Bisogni" = { name: false, summary: false,
// language: false }. ⛔ Hiding never relaxes a rule: every field is still validated on save.
//
// v201 — every empty box gives an example ("Es.: …"), the same dentist persona the AI create form uses (name and
// description reuse its `createPlaceholders`). The example follows the HALF's language — the English half reads
// English examples whatever the interface language — and a read-only form shows none. The box grows to fit it.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import GrowingTextarea, { oneLine, blockEnter } from '../brandVoice/ui/GrowingTextarea.jsx';
import { emptyItem } from './personaModel.js';
import {
  fieldLabel, fieldName, fieldError, fieldWarning, ghostBtn, iconBtn, inlineField, inlineFieldName,
  growInput, paragraphInput, nameInput,
  sectionCard, sectionTitle, sectionHint, twoColumns,
  pgCard, pgStack, pgPair, pgPairCell, pgRemove,
} from './personaStyles.js';

// v201: the example of each box, by field path (a Pain & Gain row's index is dropped: `pain_gain.items.pain`).
const PLACEHOLDERS = {
  name: 'personas.assist.createPlaceholders.name',
  'who.summary': 'personas.assist.createPlaceholders.who',
  'who.role': 'personas.placeholders.role',
  'who.context': 'personas.placeholders.context',
  'pain_gain.items.pain': 'personas.placeholders.pain',
  'pain_gain.items.gain': 'personas.placeholders.gain',
  'pain_gain.items.indicators': 'personas.placeholders.indicators',
  'pain_gain.text': 'personas.placeholders.painGainText',
  language_use: 'personas.placeholders.language_use',
  language_avoid: 'personas.placeholders.language_avoid',
};

export default function PersonaHalfForm({
  half, lang, labels, errors = {}, warnings = {}, readOnly, onChange, show = {},
}) {
  const s = { name: true, summary: true, advanced: true, language: true, ...show };
  const { t } = useTranslation(['context']);
  const err = (path) => errors[`${lang}.${path}`];
  const set = (mutate) => { const next = JSON.parse(JSON.stringify(half)); mutate(next); onChange(next); };
  const Err = ({ path }) => (err(path) ? <span role="alert" style={fieldError}>{t(`personas.errors.${err(path)}`)}</span> : null);
  const id = (path) => `persona-${lang}-${path.replace(/\./g, '-')}`;
  const example = (path) => {
    const key = PLACEHOLDERS[path.replace(/\.\d+\./, '.')];
    return readOnly || !key ? undefined : t(key, { lng: lang });
  };
  // v197: `warnings` = numbers an AI proposal wrote that no source contains, keyed by the ITALIAN path (the
  // editor passes them for the Italian half only). A note for the user, never an error: it sits right AFTER the
  // field's <label> (inside it, it would join the box's accessible name) and is tied to the box by
  // aria-describedby.
  const warnId = (path) => `${id(path)}-warn`;
  const describedBy = (path) => (warnings[path] ? warnId(path) : undefined);
  const Warn = ({ path }) => (warnings[path]
    ? <span id={warnId(path)} role="note" style={fieldWarning}>{t('personas.assist.warningField', { numbers: warnings[path].join(', ') })}</span>
    : null);

  // ONE LINE on the wire: Enter is blocked and a pasted line break folds to a
  // space, so the box can wrap freely without ever producing the `oneLine`
  // refusal. `style` lets the name field be the one bigger box.
  const line = (path, value, onVal, label, style = growInput(readOnly, Boolean(err(path)))) => (
    <>
      <label htmlFor={id(path)} style={fieldLabel}>
        <span style={fieldName}>{label}</span>
        <GrowingTextarea
          id={id(path)} value={value} readOnly={readOnly}
          aria-invalid={Boolean(err(path))}
          aria-describedby={describedBy(path)}
          placeholder={example(path)}
          fitPlaceholder
          onKeyDown={blockEnter}
          onChange={e => onVal(oneLine(e.target.value))}
          style={style}
        />
        <Err path={path} />
      </label>
      <Warn path={path} />
    </>
  );

  // PARAGRAPHS: real line breaks allowed (a blank line starts a paragraph), and
  // the box still grows with its text instead of scrolling inside a fixed height.
  const paragraph = (path, value, onVal, label) => (
    <>
      <label htmlFor={id(path)} style={fieldLabel}>
        <span style={fieldName}>{label}</span>
        <GrowingTextarea
          id={id(path)} value={value} readOnly={readOnly}
          aria-invalid={Boolean(err(path))}
          aria-describedby={describedBy(path)}
          placeholder={example(path)}
          fitPlaceholder
          onChange={e => onVal(e.target.value)}
          style={paragraphInput(readOnly, Boolean(err(path)))}
        />
        <Err path={path} />
      </label>
      <Warn path={path} />
    </>
  );

  const pg = half.pain_gain;

  return (
    <div>
      {s.name && (
        <div style={{ marginBottom: '8px' }}>
          {/* ONE row: the label beside the box (the same label column as Mercato in the editor). The error
              sits below the row, outside the <label>, so the field's name stays just "Nome". */}
          <label htmlFor={id('name')} style={inlineField}>
            <span style={inlineFieldName}>{t('personas.fields.name')}</span>
            <GrowingTextarea
              id={id('name')} value={half.name} readOnly={readOnly}
              aria-invalid={Boolean(err('name'))}
              aria-describedby={describedBy('name')}
              placeholder={example('name')}
              fitPlaceholder
              onKeyDown={blockEnter}
              onChange={e => set(h => { h.name = oneLine(e.target.value); })}
              style={{ ...nameInput(readOnly, Boolean(err('name'))), marginTop: 0, flex: '1 1 260px' }}
            />
          </label>
          <Err path="name" />
          <Warn path="name" />
        </div>
      )}

      {(s.summary || s.advanced) && (
        <section style={sectionCard}>
          <h3 style={sectionTitle}>{labels.who}</h3>
          {s.summary && paragraph('who.summary', half.who.summary, v => set(h => { h.who.summary = v; }), t('personas.fields.summary'))}
          {s.advanced && line('who.role', half.who.role, v => set(h => { h.who.role = v; }), labels.role)}
          {s.advanced && line('who.context', half.who.context, v => set(h => { h.who.context = v; }), labels.context)}
        </section>
      )}

      {s.advanced && (
      <section style={sectionCard}>
        <h3 style={sectionTitle}>{labels.pain_gain}</h3>
        {pg.format === 'list' ? (
          <div>
            <p style={sectionHint}>{t('personas.fields.painGainHint')}</p>
            {(pg.items || []).map((item, n) => (
              <div key={n} role="group" aria-label={t('personas.fields.rowLabel', { n: n + 1 })} style={pgCard}>
                {/* No visible "Riga N": the card itself is the row. The number
                    stays in the group's accessible name, so a screen reader
                    still says which row a Pain or Gain belongs to. The × is
                    pinned in the card corner (`pgRemove`), so the cells get the
                    whole card width. */}
                {/* Pain and Gain share ONE line, half the card each (they wrap
                    to a stack when the card is narrow); Indicatori takes the
                    full width below. Two plain blocks, not one grid: in a
                    grid the full-width Indicatori kept a third, empty column
                    alive and squeezed Pain and Gain into two thirds. */}
                <div style={pgStack}>
                  <div style={pgPair}>
                    <div style={pgPairCell}>
                      {line(`pain_gain.items.${n}.pain`, item.pain, v => set(h => { h.pain_gain.items[n].pain = v; }), labels.pain)}
                    </div>
                    <div style={pgPairCell}>
                      {line(`pain_gain.items.${n}.gain`, item.gain, v => set(h => { h.pain_gain.items[n].gain = v; }), labels.gain)}
                    </div>
                  </div>
                  {line(`pain_gain.items.${n}.indicators`, item.indicators, v => set(h => { h.pain_gain.items[n].indicators = v; }), t('personas.fields.indicators'))}
                </div>
                {!readOnly && (pg.items.length > 1) && (
                  <button
                    type="button"
                    aria-label={t('personas.fields.removeRow')}
                    title={t('personas.fields.removeRow')}
                    onClick={() => set(h => { h.pain_gain.items.splice(n, 1); })}
                    style={{ ...iconBtn(false), ...pgRemove }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            {err('pain_gain') && <span role="alert" style={fieldError}>{t(`personas.errors.${err('pain_gain')}`)}</span>}
            {!readOnly && (
              <button type="button" onClick={() => set(h => { h.pain_gain.items.push(emptyItem()); })} style={ghostBtn(false)}>
                {t('personas.fields.addRow')}
              </button>
            )}
          </div>
        ) : paragraph('pain_gain.text', pg.text || '', v => set(h => { h.pain_gain.text = v; }), labels.pain_gain)}
      </section>
      )}

      {s.language && (
        <section style={sectionCard}>
          <h3 style={sectionTitle}>{t('personas.fields.languageTitle')}</h3>
          <div style={twoColumns}>
            {paragraph('language_use', half.language_use, v => set(h => { h.language_use = v; }), labels.language_use)}
            {paragraph('language_avoid', half.language_avoid, v => set(h => { h.language_avoid = v; }), labels.language_avoid)}
          </div>
        </section>
      )}
    </div>
  );
}
